import { Toolbar, VSpacer } from "@/components/DesignSystem";
import { Filter, FilterSelections } from "@/components/DesignSystem/Toolbar/interfaces";
import ERPRetailerSelector from '@/components/shared/ListSelector/ERPRetailerSelector';
import { QueryKeys } from "@/constants/QueryKeys";
import { useAuthentication } from '@/contexts/dataSync/AuthenticationContext';
import { useRetailerLocationFilter } from "@/hooks/useRetailerLocationFilter";
import { useSearch } from "@/hooks/useSearch";
import { RewardsProgramApi } from '@/utilities/api/RewardsProgramApi';
import { UserApi } from "@/utilities/api/UserApi";
import { UserEndpoint } from '@api/endpoints';
import { ApiRetailer, ApiUser } from "@api/interfaces";
import { CircularProgress, Container, Pagination, Stack } from "@mui/material";
import React, { Fragment, useEffect, useMemo, useState } from "react";
import { ChurnStatus, UserType } from "@shared/enums";
import CustomerCard from "./CustomerCard";
import { useQuery } from "react-query";
import { useSearchParams } from "react-router-dom";
import { getChurnStatusFilter } from "@/utilities/ChurnCustomer";
import { ProgramExpirationBanner } from '@/components/shared/ProgramExpirationBanner';
import { useTranslation } from 'react-i18next';

enum QueryParams {
  'search' = 'search',
}

const CustomerList = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialSearch = searchParams.get(QueryParams.search) ?? '';
  const { setSearch, debouncedSearch } = useSearch(3, initialSearch);
  const [page, setPage] = useState(0);

  const [filterSelections, setFilterSelections] =
    useState<FilterSelections>(() => new Map());
  const { isInternal, isRetailerAdmin, retailer, user } = useAuthentication();
  const [selectedRetailer, setSelectedRetailer] = useState<ApiRetailer | undefined>(retailer);

  useEffect(() => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set(QueryParams.search, debouncedSearch);
      return newParams;
    });
    setPage(0);
  }, [debouncedSearch]);

  const { data: rewardsProgram } = useQuery(
    [QueryKeys.GET_ACTIVE_PROGRAM_FOR_RETAILER, selectedRetailer?.id, retailer?.id],
    () => RewardsProgramApi.getActiveProgramForRetailer((retailer?.id ?? selectedRetailer?.id)!),
    { enabled: !!(retailer?.id ?? selectedRetailer?.id) },
  );

  const tierOptions = rewardsProgram?.tiers?.map(
    (tier) => ({ id: tier.rank.toString(), label: tier.name }),
  ) ?? [];

  const stringifiedFilterSelections = JSON.stringify(
    Array.from(filterSelections).map(([, values]) => Array.from(values)),
  );

  const queryBody: UserEndpoint.List.Query = useMemo(() => {
    const selectedCurrentStatuses = Array.from(filterSelections?.get('current-status') ?? []);
    const hasNotReEarnedRank = filterSelections?.get('has-not-re-earned-status')
      ?.has('has-not-re-earned-status') || undefined;
    const hasReEarnedRank = filterSelections?.get('re-earned-tier')
      ?.has('re-earned-tier') || undefined;
    const hasPoints = filterSelections?.get('has-points')?.has('has-points') || undefined;
    const selectedLocations = Array.from(filterSelections?.get('locations') ?? []);
    const churnStatus = Array.from(filterSelections?.get('churnStatus') ?? []).map(
      (status) => status as ChurnStatus,
    );
    const selectedPointsExpiring = filterSelections?.get('points-expiring')?.values().next().value;
    const programMembership = filterSelections?.get('programMembership')?.values().next().value;

    return {
      ...(programMembership ? { programMembership } : {}),
      ...(churnStatus.length ? { churnStatus } : {}),
      ...(selectedCurrentStatuses.length
        ? { rankCurrent: selectedCurrentStatuses.map((tier) => Number(tier)) } : {}),
      ...(hasNotReEarnedRank ? { hasNotReEarnedRank } : {}),
      ...(hasReEarnedRank ? { hasReEarnedRank } : {}),
      ...(hasPoints ? { hasPoints } : {}),
      ...(selectedLocations.length ? { locationId: selectedLocations } : {}),
      ...(selectedPointsExpiring ? { daysUntilPointsExpire: selectedPointsExpiring } : {}),
    } as UserEndpoint.List.Query;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stringifiedFilterSelections]);

  const userQuery = {
    page,
    retailerId: selectedRetailer ? selectedRetailer.id : null,
    search: debouncedSearch,
    userType: [UserType.Farmer],
  };

  const salespersonQuery = {
    page,
    search: debouncedSearch,
  };

  const churnStatusFilter = getChurnStatusFilter();
  const locationFilter = useRetailerLocationFilter(selectedRetailer?.id);
  const canFilterByProgram = isInternal || isRetailerAdmin;
  const programFilter: Filter = {
    id: 'programMembership',
    label: t($ => $.customers.list.filters.program),
    options: [
      { id: 'agronomy', label: t($ => $.energy.profile.agronomy) },
      { id: 'energy', label: t($ => $.energy.profile.energy) },
    ],
    selectionMethod: 'single-select',
  };
  const filters: Filter[] = [
    ...(canFilterByProgram ? [programFilter] : []),
    ...(user?.userType === UserType.RetailerAdmin || user?.userType === UserType.Internal)
      ? [locationFilter] : [],
    ...(rewardsProgram ? [
      {
        id: 'points-expiring',
        label: t($ => $.customers.list.filters.pointsExpiring),
        options: [
          {
            id: '7',
            label: t($ => $.customers.list.filters.pointsExpiringWithinDays, { days: 7 }),
          },
          {
            id: '30',
            label: t($ => $.customers.list.filters.pointsExpiringWithinDays, { days: 30 }),
          },
          {
            id: '60',
            label: t($ => $.customers.list.filters.pointsExpiringWithinDays, { days: 60 }),
          },
          {
            id: '90',
            label: t($ => $.customers.list.filters.pointsExpiringWithinDays, { days: 90 }),
          },
        ],
        selectionMethod: 'single-select',
      },
      {
        id: 'current-status',
        label: t($ => $.customers.list.filters.currentStatus),
        options: [{ id: '-1', label: t($ => $.common.status.none) }, ...tierOptions],
        selectionMethod: 'multi-select',
      },
      {
        id: 'has-not-re-earned-status',
        label: t($ => $.customers.list.filters.hasNotReEarnedStatus),
        options: [
          {
            id: 'has-not-re-earned-status',
            label: t($ => $.customers.list.filters.hasNotReEarnedStatus),
          }],
        selectionMethod: 'boolean',
      },
      {
        id: 're-earned-tier',
        label: t($ => $.customers.list.filters.reEarnedTier),
        options: [{ id: 're-earned-tier', label: t($ => $.customers.list.filters.reEarnedTier) }],
        selectionMethod: 'boolean',
      },
      {
        id: 'has-points',
        label: t($ => $.customers.list.filters.hasPoints),
        options: [
          { id: 'has-points', label: t($ => $.customers.list.filters.hasPoints) },
        ],
        selectionMethod: 'boolean',
      },
    ] as Filter[] : []),
  ];

  if (isInternal || (isRetailerAdmin && retailer?.isChurnEnabled)) {
    filters.push(churnStatusFilter);
  }

  const isSalesperson = user?.userType === UserType.SalesPerson;
  const { data: users, isFetching } = useQuery(
    [QueryKeys.LIST_USERS, user?.id, isSalesperson ? salespersonQuery : userQuery, queryBody],
    async () => {
      if (isSalesperson && !debouncedSearch) {
        return UserApi.getFarmersAssociatedToSalesperson({ ...salespersonQuery, ...queryBody });
      } else {
        return UserApi.list({ ...userQuery, ...queryBody });
      }
    },
    { staleTime: 5 * 60 * 1000 },
  );

  return (
    <Container maxWidth="lg">
      <Stack>
        {!!rewardsProgram && (
          <ProgramExpirationBanner program={rewardsProgram}/>
        )}
        <ERPRetailerSelector
          header={t($ => $.customers.title)}
          logo="none"
          onSelect={(retailer) => setSelectedRetailer(retailer)}
        />
        <VSpacer size="6" />
        <Toolbar
          filterSelections={filterSelections}
          filters={filters}
          initialSearch={searchParams.get(QueryParams.search)}
          onChange={({ search, selections }) => {
            setSearch(search ?? '');
            if (selections) {
              const newSelections = Array.from(selections.keys());
              const oldSelections = Array.from(filterSelections.keys() ?? []);
              if (newSelections.includes('re-earned-tier')
                && !oldSelections.includes('re-earned-tier')) {
                selections.delete('has-not-re-earned-status');
              }
              if (newSelections.includes('has-not-re-earned-status')
                && !oldSelections.includes('has-not-re-earned-status')) {
                selections.delete('re-earned-tier');
              }
            }
            setFilterSelections(selections ?? new Map());
          }}
          testID="promotions-toolbar-generic"
          totalItems={users?.total}
          totalUnitLabel={{
            plural: t($ => $.common.units.customerAccounts),
            singular: t($ => $.common.units.customerAccount),
          }}
        />
        <VSpacer size="6" />
        {isFetching && (
          <Container>
            <Stack alignItems='center'>
              <CircularProgress />
            </Stack>
          </Container>
        )}
        {!isFetching && !!users && users.data.map((user) => (
          <Fragment key={user.id}>
            <CustomerCard customer={user as ApiUser}/>
            <VSpacer size="5" />
          </Fragment>
        ))}
        {!isFetching && !!users && users.lastPage !== 0 &&
          <Stack alignItems="center">
            <VSpacer size="9" />
            <Pagination
              count={users.lastPage + 1}
              onChange={(_, page) => {
                setPage(page - 1);
              }}
              page={users.page + 1}
            />
          </Stack>
        }
      </Stack>
    </Container>
  );
};

export default CustomerList;

